import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const AGBPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>AGB – unternehmenbewerten.ch</title>
        <meta name="description" content="Allgemeine Geschäftsbedingungen von unternehmenbewerten.ch, einem Service der Schweizer Nachfolge Experten AG." />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/agb" />
      </Helmet>
      
      <main className="pt-20">
        <section className="py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-[#5a5d5b] hover:text-[#FF0000] transition-colors">Start</Link>
              <span className="text-[#888b89]">/</span>
              <span className="text-[#FF0000]">AGB</span>
            </nav>

            <h1 className="text-4xl font-bold text-[#1a1c1b] mb-4">Allgemeine Geschäftsbedingungen (AGB)</h1>
            <p className="text-[#5a5d5b] mb-12">Stand: Januar 2025</p>
            
            <div className="prose prose-lg max-w-none space-y-10">
              
              {/* Geltungsbereich */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">1. Geltungsbereich</h2>
                <p className="text-[#5a5d5b] mb-4">
                  Diese Allgemeinen Geschäftsbedingungen (nachfolgend «AGB») regeln die Nutzung der Website 
                  unternehmenbewerten.ch und der darauf angebotenen Dienstleistungen der Schweizer Nachfolge 
                  Experten AG (nachfolgend «Anbieter»).
                </p>
                <p className="text-[#5a5d5b] mb-4">
                  Mit der Nutzung dieser Website und der Inanspruchnahme der angebotenen Dienstleistungen 
                  erklären Sie sich mit diesen AGB einverstanden. Wenn Sie mit diesen AGB nicht einverstanden 
                  sind, dürfen Sie diese Website nicht nutzen.
                </p>
                <p className="text-[#5a5d5b]">
                  Der Anbieter behält sich das Recht vor, diese AGB jederzeit zu ändern. Es gilt jeweils 
                  die zum Zeitpunkt der Nutzung gültige Fassung.
                </p>
              </section>

              {/* Leistungsbeschreibung */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">2. Leistungsbeschreibung</h2>
                
                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">2.1 Online-Bewertungstool</h3>
                <p className="text-[#5a5d5b] mb-4">
                  Der Anbieter stellt ein kostenfreies Online-Tool zur indikativen Unternehmensbewertung 
                  zur Verfügung. Das Tool ermöglicht eine erste Einschätzung des Unternehmenswertes basierend 
                  auf den vom Nutzer eingegebenen Daten.
                </p>

                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">2.2 Art der Bewertung</h3>
                <p className="text-[#5a5d5b] mb-4">
                  Die durch das Tool berechneten Bewertungsergebnisse sind:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2">
                  <li>Rein indikativ und unverbindlich</li>
                  <li>Basierend auf standardisierten Bewertungsmethoden</li>
                  <li>Abhängig von der Richtigkeit und Vollständigkeit der eingegebenen Daten</li>
                  <li>Keine Garantie für den tatsächlich erzielbaren Marktwert</li>
                </ul>
              </section>

              {/* WICHTIGER HINWEIS */}
              <section className="bg-[#FEF2F2] border border-[#FF0000]/20 rounded-xl p-6">
                <h2 className="text-2xl font-bold text-[#CC0000] mb-4">3. WICHTIGER HINWEIS – Keine Kauf- oder Verkaufsempfehlung</h2>
                <p className="text-[#5a5d5b] mb-4">
                  <strong className="text-[#1a1c1b]">Die auf dieser Website bereitgestellten Unternehmensbewertungen 
                  stellen ausdrücklich KEINE Kauf-, Verkaufs- oder Investitionsempfehlung dar.</strong>
                </p>
                <p className="text-[#5a5d5b] mb-4">
                  Die Bewertungsergebnisse:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2 mb-4">
                  <li>Sind keine Finanz-, Rechts- oder Steuerberatung</li>
                  <li>Ersetzen keine professionelle Due-Diligence-Prüfung</li>
                  <li>Sind keine Grundlage für Investitionsentscheidungen</li>
                  <li>Stellen keine Wertgarantie oder Preisempfehlung dar</li>
                  <li>Sind nicht als Gutachten im rechtlichen Sinne zu verstehen</li>
                  <li>Sind nicht für Banken, Behörden oder Gerichte bestimmt</li>
                </ul>
                <p className="text-[#5a5d5b]">
                  <strong className="text-[#1a1c1b]">Vor jeder unternehmerischen Entscheidung (Kauf, Verkauf, 
                  Finanzierung, Nachfolge) empfehlen wir dringend die Konsultation qualifizierter Fachberater 
                  (Wirtschaftsprüfer, Steuerberater, Rechtsanwälte, M&A-Berater).</strong>
                </p>
              </section>

              {/* Haftungsausschluss */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">4. Haftungsausschluss</h2>
                
                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">4.1 Allgemeiner Haftungsausschluss</h3>
                <p className="text-[#5a5d5b] mb-4">
                  Der Anbieter übernimmt keinerlei Gewähr für:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2">
                  <li>Die Richtigkeit, Vollständigkeit und Aktualität der bereitgestellten Informationen und Bewertungsergebnisse</li>
                  <li>Die Eignung der Bewertungsergebnisse für bestimmte Zwecke</li>
                  <li>Die ununterbrochene Verfügbarkeit der Website und des Bewertungstools</li>
                  <li>Die Freiheit der Website von Viren oder anderen schädlichen Komponenten</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">4.2 Ausschluss von Schadenersatzansprüchen</h3>
                <p className="text-[#5a5d5b] mb-4">
                  Jegliche Haftung für Schäden, die aus der Nutzung dieser Website oder des Bewertungstools 
                  entstehen, ist ausgeschlossen. Dies gilt insbesondere für:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2">
                  <li>Direkte und indirekte Schäden</li>
                  <li>Folgeschäden und entgangenen Gewinn</li>
                  <li>Schäden aus Datenverlust</li>
                  <li>Schäden aus unternehmerischen Entscheidungen, die auf Basis der Bewertungsergebnisse getroffen wurden</li>
                  <li>Schäden aus fehlgeschlagenen Transaktionen</li>
                  <li>Schäden durch Dritte</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">4.3 Keine Haftung für Dritte</h3>
                <p className="text-[#5a5d5b]">
                  Der Anbieter haftet nicht für Handlungen oder Unterlassungen Dritter, insbesondere nicht 
                  für den Inhalt verlinkter Websites oder für Schäden, die durch Dritte verursacht werden.
                </p>
              </section>

              {/* Nutzungsbedingungen */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">5. Nutzungsbedingungen</h2>
                
                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">5.1 Korrekte Datenangabe</h3>
                <p className="text-[#5a5d5b] mb-4">
                  Der Nutzer verpflichtet sich, bei der Nutzung des Bewertungstools wahrheitsgemässe und 
                  vollständige Angaben zu machen. Der Anbieter haftet nicht für Bewertungsergebnisse, 
                  die auf falschen oder unvollständigen Angaben beruhen.
                </p>

                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">5.2 Bestimmungsgemässe Nutzung</h3>
                <p className="text-[#5a5d5b] mb-4">
                  Die Nutzung der Website und des Bewertungstools ist ausschliesslich für legale Zwecke 
                  gestattet. Untersagt ist insbesondere:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2">
                  <li>Die Nutzung für betrügerische oder irreführende Zwecke</li>
                  <li>Die automatisierte Abfrage oder Auslesen von Daten (Scraping)</li>
                  <li>Die Überlastung der Server durch übermässige Anfragen</li>
                  <li>Die Umgehung von Sicherheitsmassnahmen</li>
                  <li>Die Verbreitung von Schadsoftware</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#1a1c1b] mb-3 mt-6">5.3 Eigenverantwortung</h3>
                <p className="text-[#5a5d5b]">
                  Der Nutzer trägt die alleinige Verantwortung für alle Entscheidungen, die er auf Basis 
                  der Bewertungsergebnisse trifft. Der Anbieter empfiehlt ausdrücklich, vor wichtigen 
                  unternehmerischen Entscheidungen professionelle Beratung einzuholen.
                </p>
              </section>

              {/* Geistiges Eigentum */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">6. Geistiges Eigentum</h2>
                <p className="text-[#5a5d5b] mb-4">
                  Sämtliche Inhalte dieser Website (Texte, Grafiken, Logos, Bilder, Software, 
                  Bewertungsmethoden, Algorithmen) sind urheberrechtlich geschützt und Eigentum des 
                  Anbieters oder der jeweiligen Rechteinhaber.
                </p>
                <p className="text-[#5a5d5b] mb-4">
                  Ohne ausdrückliche schriftliche Genehmigung ist untersagt:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2">
                  <li>Die Vervielfältigung, Verbreitung oder öffentliche Wiedergabe von Inhalten</li>
                  <li>Die Modifikation oder Bearbeitung von Inhalten</li>
                  <li>Die kommerzielle Nutzung von Inhalten</li>
                  <li>Das Reverse Engineering des Bewertungstools</li>
                </ul>
              </section>

              {/* Vertraulichkeit */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">7. Vertraulichkeit</h2>
                <p className="text-[#5a5d5b] mb-4">
                  Der Anbieter behandelt alle im Rahmen der Bewertung übermittelten Unternehmensdaten 
                  vertraulich. Die Daten werden nicht ohne Zustimmung des Nutzers an Dritte weitergegeben, 
                  es sei denn, dies ist gesetzlich vorgeschrieben.
                </p>
                <p className="text-[#5a5d5b]">
                  Weitere Informationen zum Umgang mit personenbezogenen Daten finden Sie in unserer 
                  <Link to="/datenschutz" className="text-[#FF0000] hover:underline ml-1">Datenschutzerklärung</Link>.
                </p>
              </section>

              {/* Keine Vertragsbeziehung */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">8. Keine Vertragsbeziehung</h2>
                <p className="text-[#5a5d5b] mb-4">
                  Die Nutzung des kostenfreien Bewertungstools begründet kein Vertragsverhältnis zwischen 
                  dem Nutzer und dem Anbieter. Es besteht kein Anspruch auf:
                </p>
                <ul className="list-disc pl-6 text-[#5a5d5b] space-y-2">
                  <li>Durchführung einer Bewertung</li>
                  <li>Bestimmte Bewertungsergebnisse</li>
                  <li>Fortgesetzte Verfügbarkeit des Dienstes</li>
                  <li>Support oder Beratung</li>
                </ul>
              </section>

              {/* Änderungen und Einstellung */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">9. Änderungen und Einstellung des Dienstes</h2>
                <p className="text-[#5a5d5b]">
                  Der Anbieter behält sich das Recht vor, das Bewertungstool und die Website jederzeit 
                  und ohne Vorankündigung zu ändern, zu unterbrechen oder einzustellen. Es besteht kein 
                  Anspruch auf Aufrechterhaltung des Dienstes in seiner aktuellen Form.
                </p>
              </section>

              {/* Salvatorische Klausel */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">10. Salvatorische Klausel</h2>
                <p className="text-[#5a5d5b]">
                  Sollten einzelne Bestimmungen dieser AGB unwirksam oder undurchführbar sein oder werden, 
                  so bleibt die Wirksamkeit der übrigen Bestimmungen davon unberührt. Anstelle der 
                  unwirksamen oder undurchführbaren Bestimmung gilt eine wirksame und durchführbare 
                  Bestimmung als vereinbart, die dem wirtschaftlichen Zweck der unwirksamen oder 
                  undurchführbaren Bestimmung am nächsten kommt.
                </p>
              </section>

              {/* Anwendbares Recht */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">11. Anwendbares Recht und Gerichtsstand</h2>
                <p className="text-[#5a5d5b] mb-4">
                  Diese AGB und alle damit zusammenhängenden Streitigkeiten unterliegen ausschliesslich 
                  schweizerischem Recht unter Ausschluss der Kollisionsnormen und des UN-Kaufrechts.
                </p>
                <p className="text-[#5a5d5b]">
                  Ausschliesslicher Gerichtsstand für alle Streitigkeiten ist Zürich, Schweiz. 
                  Der Anbieter behält sich jedoch das Recht vor, Klagen am Wohnsitz des Nutzers zu erheben.
                </p>
              </section>

              {/* Kontakt */}
              <section>
                <h2 className="text-2xl font-bold text-[#1a1c1b] mb-4">12. Kontakt</h2>
                <p className="text-[#5a5d5b] mb-4">
                  Bei Fragen zu diesen AGB wenden Sie sich bitte an:
                </p>
                <div className="bg-[#f8f9fa] rounded-xl p-6">
                  <p className="font-semibold text-[#1a1c1b]">Schweizer Nachfolge Experten AG</p>
                  <p className="text-[#5a5d5b]">c/o unternehmenbewerten.ch</p>
                  <p className="text-[#5a5d5b]">Löwenstrasse 20</p>
                  <p className="text-[#5a5d5b]">8001 Zürich, Schweiz</p>
                  <p className="text-[#5a5d5b] mt-2">E-Mail: <a href="mailto:info@unternehmenbewerten.ch" className="hover:text-[#FF0000] transition-colors" onClick={() => { if (typeof window !== 'undefined' && window.gtag) { window.gtag('event', 'contact_email_click', { event_category: 'contact', method: 'email' }); } }}>info@unternehmenbewerten.ch</a></p>
                  <p className="text-[#5a5d5b]">Telefon: <a href="tel:+41582205550" className="hover:text-[#FF0000] transition-colors" onClick={() => { if (typeof window !== 'undefined' && window.gtag) { window.gtag('event', 'contact_phone_click', { event_category: 'contact', method: 'phone' }); } }}>+41 58 220 55 50</a></p>
                </div>
              </section>

            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AGBPage;
